import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";
import { SectionTitle } from "../analytics/AnalyticsShared";
import { InsightCard, FactRow, KindTag, Tag } from "./BrainShared";

const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const fmtTs = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const ORDER = { TRIGGERED: 0, INSUFFICIENT_DATA: 1, NOT_TRIGGERED: 2 };
const SOURCE_TAG = { gemini: ["Gemini · validated", "bg-primary/10 text-primary border-primary/30"], deterministic_fallback: ["Deterministic fallback", "bg-slate-500/10 text-slate-500 border-slate-500/30"] };

const Narrative = ({ n }) => (
  <div className="p-5 rounded-xl border border-border bg-card" data-testid="brain-narrative">
    <div className="flex flex-wrap items-center gap-2">
      <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} />
      <span className="text-xs uppercase tracking-wider font-mono-tech text-muted-foreground" data-testid="brain-narrative-label">{n.label}</span>
      <Tag cls={SOURCE_TAG[n.source][1]} testId="brain-narrative-source">{SOURCE_TAG[n.source][0]}</Tag>
    </div>
    <p className="mt-3 text-sm leading-relaxed" data-testid="brain-narrative-text">{n.text}</p>
    {n.validation.status !== "ACCEPTED" && (
      <p className="mt-2 text-xs text-muted-foreground" data-testid="brain-narrative-validation">
        AI text {n.validation.status === "REJECTED" ? "rejected by validator" : "unavailable"}: {n.validation.reasons.join("; ")}. Showing deterministic text instead.
      </p>
    )}
  </div>
);

export const BrainInsights = () => {
  const [index, setIndex] = useState(null);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => adminApi.get("/admin/brain/insights").then((r) => { setIndex(r.data); setSnap(r.data.latest); }).catch(() => setError("Could not load Brain insights."));
  useEffect(() => { load(); }, []);

  const select = (id) => adminApi.get(`/admin/brain/insights/${id}`).then((r) => setSnap(r.data)).catch(() => toast.error("Snapshot not found."));
  const generate = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/brain/insights/generate", {});
      toast.success(`Insights generated for week of ${fmtDay(r.data.week_start)}`);
      setSnap(r.data);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const facts = useMemo(() => Object.fromEntries((snap?.facts || []).map((f) => [f.id, f])), [snap]);
  const insights = useMemo(() => [...(snap?.insights || [])].sort((a, b) => ORDER[a.status] - ORDER[b.status]), [snap]);

  return (
    <div data-testid="brain-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">Weekly operational insights derived from S7.1 deterministic analytics. Every INSIGHT is a fixed rule over verified FACTs; the AI only rephrases. No predictions, rankings or recommendations.</p>
        <div className="flex items-center gap-2">
          {index?.history?.length > 0 && (
            <Select value={snap?.id || ""} onValueChange={select}>
              <SelectTrigger className="w-[190px] h-9" data-testid="brain-week-select"><SelectValue placeholder="Select week" /></SelectTrigger>
              <SelectContent>
                {index.history.map((h) => <SelectItem key={h.id} value={h.id} data-testid={`brain-week-option-${h.id}`}>Week of {fmtDay(h.week_start)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={generate} disabled={busy} className="h-9 rounded-full" data-testid="brain-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}{busy ? "Generating…" : "Generate this week"}
          </Button>
        </div>
      </div>

      {error && <p className="mt-6 text-sm text-destructive" data-testid="brain-error">{error}</p>}
      {!index && !error && <div className="py-16 flex justify-center" data-testid="brain-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>}
      {index && !snap && (
        <p className="mt-6 text-sm text-muted-foreground p-6 rounded-xl border border-dashed border-border" data-testid="brain-empty">No weekly snapshot yet. Generate the current week to see verified facts and rule-based insights.</p>
      )}

      {snap && (
        <div className="mt-6" data-testid="brain-snapshot">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground font-mono-tech" data-testid="brain-snapshot-meta">
            <span data-testid="brain-week-label">Week {fmtDay(snap.week_start)} – {fmtDay(snap.week_end)} (UTC)</span>
            <span>Baseline: {snap.windows.baseline.weeks} weeks before</span>
            <span data-testid="brain-generated-at">Generated {fmtTs(snap.generated_at)} by {snap.generated_by}</span>
            <span>{snap.method} · source {snap.source}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="brain-counts">
            <Tag cls="bg-orange-500/10 text-orange-600 border-orange-500/30" testId="brain-count-triggered">{snap.counts.triggered} triggered</Tag>
            <Tag cls="bg-slate-500/10 text-slate-500 border-dashed border-slate-500/40" testId="brain-count-insufficient">{snap.counts.insufficient_data} insufficient data</Tag>
            <Tag cls="bg-emerald-500/10 text-emerald-600 border-emerald-500/30" testId="brain-count-not-triggered">{snap.counts.not_triggered} not triggered</Tag>
            <Tag cls="bg-slate-500/10 text-slate-600 border-slate-500/30" testId="brain-count-facts">{snap.counts.facts} facts</Tag>
          </div>

          <SectionTitle>Narrative</SectionTitle>
          <Narrative n={snap.narrative} />

          <SectionTitle>Insights</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-4" data-testid="brain-insights-list">
            {insights.map((i) => <InsightCard key={i.id} insight={i} facts={facts} />)}
          </div>

          <SectionTitle>Verified facts (S7.1)</SectionTitle>
          <div className="p-4 rounded-xl border border-border bg-card" data-testid="brain-facts-table">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><KindTag kind="FACT" /> Values read verbatim from deterministic analytics · current week vs baseline</div>
            {snap.facts.map((f) => <FactRow key={f.id} fact={f} />)}
          </div>
        </div>
      )}
    </div>
  );
};
